import React from 'react';
import {
  ActivityIndicator,
  Pressable,
  Text,
  type PressableProps,
  type TextProps,
} from 'react-native';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';

const variantClasses: Record<
  ButtonVariant,
  { pressable: string; text: string; disabled: string }
> = {
  primary: {
    pressable:
      'rounded-2xl border border-zinc-900 bg-zinc-900 px-5 py-3.5 active:opacity-90 dark:border-zinc-100 dark:bg-zinc-50',
    text: 'text-center text-base font-semibold text-white dark:text-zinc-950',
    disabled: 'opacity-50',
  },
  secondary: {
    pressable:
      'rounded-2xl border border-zinc-200 bg-zinc-100 px-5 py-3.5 active:opacity-90 dark:border-zinc-700 dark:bg-zinc-800',
    text: 'text-center text-base font-semibold text-zinc-900 dark:text-zinc-100',
    disabled: 'opacity-50',
  },
  outline: {
    pressable:
      'rounded-2xl border border-zinc-300 bg-transparent px-5 py-3.5 active:opacity-80 dark:border-zinc-600',
    text: 'text-center text-base font-semibold text-zinc-800 dark:text-zinc-200',
    disabled: 'opacity-50',
  },
  ghost: {
    pressable: 'rounded-2xl px-4 py-3 active:opacity-70',
    text: 'text-center text-base font-medium text-zinc-600 dark:text-zinc-400',
    disabled: 'opacity-40',
  },
};

export type ButtonProps = PressableProps & {
  variant?: ButtonVariant;
  loading?: boolean;
  children: React.ReactNode;
  textClassName?: string;
  textProps?: TextProps;
};

export function Button({
  variant = 'primary',
  loading,
  disabled,
  children,
  className,
  textClassName,
  textProps,
  ...rest
}: ButtonProps) {
  const v = variantClasses[variant];
  const isDisabled = Boolean(disabled || loading);
  return (
    <Pressable
      accessibilityRole="button"
      disabled={isDisabled}
      className={`${v.pressable} ${isDisabled ? v.disabled : ''} ${className ?? ''}`}
      {...rest}>
      {loading ? (
        <ActivityIndicator color={variant === 'primary' ? '#ffffff' : '#18181b'} />
      ) : (
        <Text className={`${v.text} ${textClassName ?? ''}`} {...textProps}>
          {children}
        </Text>
      )}
    </Pressable>
  );
}
